import { EnvService } from '../../core/helpers/env.service';
import {
    Component,
    inject,
    ElementRef,
    HostListener,
    NgZone,
    OnInit,
    ViewChild,
} from '@angular/core';
import { RouterPath } from '../../shared/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DpfService } from '../../core/services/dpf.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AppStateService } from '../../core/services/app-state.service';
import { LocaleService } from '../../core/services/locale.service';
import { ConfirmModalService } from '../../shared/modals/confirm-modal/confirm-modal.service';
import { GoogleAuthService } from '../../core/services/google-auth.service';
import { DocsService } from '../../views/docs/docs.service';
import { KeycloakPkceService } from '../../core/services/keycloak-pkce.service';

/** Desktop nav dropdown keys (product, news, profile, lang in main nav). */
type DesktopNavDropdownKey = 'productDropdown' | 'newsDropdown' | 'profileDropdown' | 'langDropdown';
/** Nav dropdown keys (product, news, profile, lang in main nav / mobile). */
type MobileNavDropdownKey = 'product' | 'news' | 'profile' | 'lang';
/** Any header panel. */
type HeaderPanel = DesktopNavDropdownKey | 'mobileNav' | MobileNavDropdownKey;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
    @ViewChild('dropdownRef') dropdownRef!: ElementRef;
    currentLocale = 'en'; // Unified locale using ISO language codes ('en' or 'vi')
    protected formGroup: FormGroup;
    token = localStorage.getItem('token');
    userName = localStorage.getItem('userName') || '';
    isHome = true;
    /** true at init so header has blue background until ngOnInit; then transparent only when on home */
    isScrolled = true;

    /** Mobile overlay nav open state. */
    mobileNavOpen = false;
    /** Which nav dropdown(s) are open (product / news / profile / lang). */
    mobileNavDropdownOpen: Record<MobileNavDropdownKey, boolean> = {
        product: false,
        news: false,
        profile: false,
        lang: false
    };
    /** Desktop product nav dropdown open state. */
    productDropdownOpen = false;
    /** Desktop product nav dropdown stays open until trigger clicked again. */
    productDropdownPinned = false;
    /** Desktop news nav dropdown open state. */
    newsDropdownOpen = false;
    /** Desktop news nav dropdown stays open until trigger clicked again. */
    newsDropdownPinned = false;
    /** Desktop language selector dropdown open state. */
    langDropdownOpen = false;
    /** Language dropdown stays open until trigger clicked again. */
    langDropdownPinned = false;
    /** Desktop profile menu (avatar) dropdown open state. */
    profileDropdownOpen = false;
    /** Desktop profile menu (avatar) dropdown stays open until trigger clicked again. */
    profileDropdownPinned = false;

    showSetupProfile = false;
    showLogin = false;
    showSignup = false;
    showOtp = false;
    showForgotPassword = false;
    /** True while the Keycloak code exchange is in flight; login buttons are disabled meanwhile. */
    loginLoading = false;
    notificationPanelEvent = 0;

    readonly hpcEnabled = inject(EnvService).HPC_ENABLED !== false;

    protected readonly RouterPath = RouterPath;

    @HostListener('window:scroll', [])
    onWindowScroll() {
        if (this.isHome) {
            this.isScrolled = window.scrollY > 10;
        }
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(_event: Event) {
        this.ngZone.run(() => this.closeAllPanels());
    }

    constructor(
        private fb: FormBuilder,
        private dpfService: DpfService,
        public router: Router,
        private appStateService: AppStateService,
        private ngZone: NgZone,
        private localeService: LocaleService,
        private googleAuth: GoogleAuthService,
        public confirmModalService: ConfirmModalService,
        private docsService: DocsService,
        private keycloakPkce: KeycloakPkceService,
    ) {
        this.formGroup = this.fb.group({
            username: [
                '',
                [Validators.required, Validators.pattern('^[a-zA-Z0-9_]+$')],
            ],
            password: ['', [Validators.required]],
        });
    }

    ngOnInit() {
        if (localStorage.getItem('token')) {
            this.dpfService.authCheck();
        }
        this.appStateService.tokenObservable().subscribe((val) => {
            if (val) {
                this.token = localStorage.getItem('token');
            }
        });
        this.appStateService.loginObservable().subscribe((val) => {
            if (val) {
                this.showLogin = true;
            }
        });
        this.appStateService.loginLoadingObservable().subscribe((val) => {
            this.loginLoading = val;
        });
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                // Check if we're on the home page (considering language prefix)
                const path = window.location.pathname;
                const isHomePage = path === '/' || /^\/(en|vi)\/?$/.test(path);
                this.isHome = isHomePage;
                this.isScrolled = isHomePage ? window.scrollY > 10 : true;
            });
        // Initialize from translation service (which handles URL and storage)
        this.currentLocale = this.localeService.getCurrentLocale();

        // Subscribe to language changes
        this.localeService.getCurrentLocale$().subscribe(lang => {
            this.currentLocale = lang;
        });

        // Auto-prompt Google One Tap sign-in for first-time visitors in this session
        // this.promptGoogleOneTapIfNeeded();
    }

    /**
     * Automatically prompts Google One Tap sign-in for users who:
     * 1. Are not already logged in
     * 2. Haven't been prompted in this browser session yet
     *
     * Uses sessionStorage so the prompt shows once per tab/session. FedCM (mandatory since
     * Aug 2025) silently no-ops if the user has no active Google browser session — no error
     * to catch, the prompt just won't display. Google's built-in backoff also prevents spam
     * if the user dismisses it.
     */
    private promptGoogleOneTapIfNeeded(): void {
        // Skip auto sign-in prompt in local development
        const isLocalDev = !document.querySelector('meta[name="build-commit"]')?.getAttribute('content');
        if (isLocalDev) {
            console.warn('%c[Google One Tap] Skipped auto sign-in prompt in Local Development', 'color: #FF9800;');
            return;
        }

        const isLoggedIn = !!localStorage.getItem('token');
        const hasPromptedThisSession = sessionStorage.getItem('googleOneTapPrompted');

        if (isLoggedIn || hasPromptedThisSession) {
            return;
        }

        // Mark as prompted for this session
        sessionStorage.setItem('googleOneTapPrompted', 'true');

        // Small delay to ensure page is fully loaded and Google SDK is ready
        setTimeout(() => this.loginWithGoogle(), 1000);
    }

    loginWithGoogle() {
        this.googleAuth.init(async (idToken) => {
            const body = { idToken };
            const res = await this.dpfService.loginWithGoogle(body);
            if (!res?.status) {
                return;
            }
            localStorage.setItem('token', res.data);
            await this.dpfService.authCheck(); // Call this after token is retrieved
            this.appStateService.nextTokenValue(true); // Call this after jwt is retrieved from authCheck()
            await this.docsService.acceptActivePoliciesIfNeeded(localStorage.getItem('userId'));
            const resp = await this.dpfService.checkProfile();
            if (resp?.status && resp?.data) {
                this.showSetupProfile = true;
            }
            // Close login modal if it was open (e.g. user hit guarded route then signed in via One Tap)
            this.showLogin = false;
        });

        this.googleAuth.promptOneTap();
    }

    /**
     * Set a header panel open or closed.
     * @param panel Which panel (mobileNav, nav dropdown key, profileDropdown, langDropdown).
     * @param isOpen True to open, false to close.
     */
    setPanel(panel: HeaderPanel, isOpen: boolean): void {
        this.notificationPanelEvent++;

        if (panel === 'productDropdown') {
            this.productDropdownOpen = isOpen;
            this.newsDropdownPinned = false;
            this.newsDropdownOpen = false;
            this.langDropdownPinned = false;
            this.langDropdownOpen = false;
            this.profileDropdownPinned = false;
            this.profileDropdownOpen = false;
            return;
        }
        if (panel === 'newsDropdown') {
            this.newsDropdownOpen = isOpen;
            this.productDropdownPinned = false;
            this.productDropdownOpen = false;
            this.langDropdownPinned = false;
            this.langDropdownOpen = false;
            this.profileDropdownPinned = false;
            this.profileDropdownOpen = false;
            return;
        }
        if (panel === 'profileDropdown') {
            this.profileDropdownOpen = isOpen;
            this.productDropdownPinned = false;
            this.productDropdownOpen = false;
            this.newsDropdownPinned = false;
            this.newsDropdownOpen = false;
            this.langDropdownPinned = false;
            this.langDropdownOpen = false;
            return;
        }
        if (panel === 'langDropdown') {
            this.langDropdownOpen = isOpen;
            this.productDropdownPinned = false;
            this.productDropdownOpen = false;
            this.newsDropdownPinned = false;
            this.newsDropdownOpen = false;
            this.profileDropdownPinned = false;
            this.profileDropdownOpen = false;
            return;
        }
        if (panel === 'mobileNav') {
            this.mobileNavOpen = isOpen;
            if (!isOpen) {
                this.showLogin = false;
                this.showOtp = false;
                this.showSignup = false;
            }
            return;
        }
        // Nav dropdown: product | news | profile | lang
        if (isOpen) {
            this.mobileNavDropdownOpen = {
                product: panel === 'product',
                news: panel === 'news',
                profile: panel === 'profile',
                lang: panel === 'lang'
            };
        } else {
            this.mobileNavDropdownOpen[panel] = false;
        }
    }

    /** Close all dropdowns and unpin; does not close mobile nav. */
    closeAllPanels(): void {
        this.productDropdownPinned = false;
        this.productDropdownOpen = false;
        this.newsDropdownPinned = false;
        this.newsDropdownOpen = false;
        this.langDropdownPinned = false;
        this.langDropdownOpen = false;
        this.profileDropdownPinned = false;
        this.profileDropdownOpen = false;
        this.mobileNavDropdownOpen = {
            product: false,
            news: false,
            profile: false,
            lang: false
        };
    }

    /**
     * Toggle a panel (used for click handlers).
     * For nav dropdowns, opens this one and closes others; optionally stops propagation.
     */
    togglePanel(panel: HeaderPanel, event?: Event): void {
        this.notificationPanelEvent++;
        event?.stopPropagation();
        if (panel === 'productDropdown') {
            this.productDropdownPinned = !this.productDropdownPinned;
            this.productDropdownOpen = this.productDropdownPinned;
            return;
        }
        if (panel === 'newsDropdown') {
            this.newsDropdownPinned = !this.newsDropdownPinned;
            this.newsDropdownOpen = this.newsDropdownPinned;
            return;
        }
        if (panel === 'langDropdown') {
            this.langDropdownPinned = !this.langDropdownPinned;
            this.langDropdownOpen = this.langDropdownPinned;
            return;
        }
        if (panel === 'profileDropdown') {
            this.profileDropdownPinned = !this.profileDropdownPinned;
            this.profileDropdownOpen = this.profileDropdownPinned;
            return;
        }
        if (panel === 'mobileNav') {
            this.setPanel('mobileNav', !this.mobileNavOpen);
            return;
        }
        // Nav dropdown: product/news pin like lang/profile; profile/lang (mobile) just toggle open
        if (panel === 'product') {
            this.productDropdownPinned = !this.productDropdownPinned;
            this.mobileNavDropdownOpen.product = this.productDropdownPinned;
            this.mobileNavDropdownOpen.news = false;
            this.mobileNavDropdownOpen.profile = false;
            this.mobileNavDropdownOpen.lang = false;
            return;
        }
        if (panel === 'news') {
            this.newsDropdownPinned = !this.newsDropdownPinned;
            this.mobileNavDropdownOpen.news = this.newsDropdownPinned;
            this.mobileNavDropdownOpen.product = false;
            this.mobileNavDropdownOpen.profile = false;
            this.mobileNavDropdownOpen.lang = false;
            return;
        }
        this.mobileNavDropdownOpen[panel] = !this.mobileNavDropdownOpen[panel];
        const others: MobileNavDropdownKey[] = ['product', 'news', 'profile', 'lang'].filter(k => k !== panel) as MobileNavDropdownKey[];
        others.forEach(k => (this.mobileNavDropdownOpen[k] = false));
    }

    getBlogUrl(): string {
        return `https://blog.datacore.vn/${this.currentLocale}/`;
    }

    getNewsUrl(): string {
        switch (this.currentLocale) {
            case 'vi':
                return 'https://blog.datacore.vn/vi/news-2';
            case 'en':
                return 'https://blog.datacore.vn/en/news';
            default:
                return `https://blog.datacore.vn/${this.currentLocale}/news`;
        }
    }

    /**
     * Computed display value for language selector UI
     * Returns 'ENG' or 'VIE' based on currentLocale
     */
    get langDisplay(): string {
        return this.currentLocale === 'vi' ? 'VIE' : 'ENG';
    }

    /**
     * Handle language selection (works for both desktop and mobile)
     * Updates the URL path to reflect the new language (e.g., /en/... to /vi/...)
     * @param lang Display language value ('ENG' or 'VIE')
     */
    selectLanguage(lang: 'ENG' | 'VIE') {
        const localeCode = lang === 'VIE' ? 'vi' : 'en';
        this.langDropdownPinned = false;
        this.setPanel('langDropdown', false);
        if (localeCode !== this.currentLocale) {
            this.localeService.switchLocale(localeCode);
            this.appStateService.nextLocaleValue(localeCode);
        }
    }

    /** Close panel on mouse leave unless it is pinned. */
    onMouseLeave(panel: DesktopNavDropdownKey): void {
        const pinned = panel === 'langDropdown' ? this.langDropdownPinned
            : panel === 'profileDropdown' ? this.profileDropdownPinned
            : panel === 'productDropdown' ? this.productDropdownPinned
            : panel === 'newsDropdown' ? this.newsDropdownPinned
            : true;
        if (!pinned) this.setPanel(panel, false);
    }

    validateInput(event: KeyboardEvent) {
        const regex = /^[a-zA-Z0-9 _]+$/; // thêm _ vào đây
        const key = String.fromCharCode(event.keyCode || event.which);
        if (!regex.test(key)) {
            event.preventDefault();
        }
    }

    profile() {
        this.profileDropdownPinned = false;
        this.setPanel('profileDropdown', false);
        this.localeService.navigate(RouterPath.PROFILE);
    }

    orderHistory() {
        this.profileDropdownPinned = false;
        this.setPanel('profileDropdown', false);
        this.localeService.navigate([RouterPath.PROFILE, RouterPath.ORDER_HISTORY]);
    }

    login() {
        if (this.loginLoading) return;
        this.closeAllPanels();
        this.setPanel('mobileNav', false);
        this.keycloakPkce.startLogin();
    }

    async logout() {
        this.profileDropdownPinned = false;
        this.setPanel('profileDropdown', false);
        this.setPanel('mobileNav', false);
        await this.dpfService.logout(); // Clears the user's data and reloads the home page
    }

    register() {
        this.localeService.navigate(RouterPath.REGISTER);
    }
}
